import {useQuery} from "@tanstack/react-query";
import {Asset, AssetsApi} from "@devour/client";
import getConfig from "@/utils/getConfig";

/**
 * Batching layer: collects asset IDs within a single microtask tick,
 * then fetches them all in one GET /assets/batch?ids=... call.
 * Singleton — shared across all useAsset() callers in the same page lifecycle.
 */
let pendingIds: Array<string> = [];
let pendingResolvers: Array<{
    id: string;
    resolve: (asset: Asset | null) => void;
    reject: (err: unknown) => void;
}> = [];
let batchScheduled = false;

async function flushBatch() {
    const ids = [...new Set(pendingIds)];
    const resolvers = [...pendingResolvers];
    pendingIds = [];
    pendingResolvers = [];
    batchScheduled = false;

    try {
        const response = await new AssetsApi(getConfig()).getAssetsBatch({ids});
        const assetMap = new Map<string, Asset>();
        for (const asset of response.assets) {
            assetMap.set(asset.id, asset);
        }

        for (const {id, resolve} of resolvers) {
            /*
             * Return null (not undefined) for an id the batch didn't return — a deleted/dangling
             * asset ref must not surface as an error. React Query rejects an undefined queryFn
             * result ("Query data cannot be undefined") and puts the query into an error state;
             * the app's global query-error handling then shows the "Unable to Complete" modal
             * (e.g. over the lootbox reveal). Returning null is a valid success value ("no asset"),
             * so consumers just render no image instead of erroring.
             */
            resolve(assetMap.get(id) ?? null);
        }
    } catch (err) {
        for (const {reject} of resolvers) {
            reject(err);
        }
    }
}

function fetchAssetBatched(assetId: string): Promise<Asset | null> {
    return new Promise((resolve, reject) => {
        pendingIds.push(assetId);
        pendingResolvers.push({id: assetId, resolve, reject});

        if (!batchScheduled) {
            batchScheduled = true;
            queueMicrotask(flushBatch);
        }
    });
}

async function fetchAsset(assetId: string): Promise<Asset | null> {
    if (!assetId) {
        return null;
    }

    return fetchAssetBatched(assetId);
}

function assetQuery(assetId: string) {
    return {
        queryKey: [
            "asset",
            assetId,
        ],
        queryFn: () => fetchAsset(assetId),
        keepPreviousData: true,
        // 30 min stale time — assets are immutable CDN objects; content-addressed by ID
        staleTime: 30 * 60000,
        refetchOnWindowFocus: false,
        enabled: Boolean(assetId),
    } as const;
}

export function useGetAsset(assetId: string) {
    return useQuery(assetQuery(assetId));
}
